import { CodePanel, type Chunk } from "@/components/guide/code-panel";
import type { Chapter } from "@/components/guide/tutorial";

/**
 * The "on your own machine" guide: the simulator as a Python package, and what having it
 * makes possible. Five chapters: install it; a seed is a maze; what is different locally and
 * why that is fine; the simulator's own API; reinforcement learning on it. The previews are
 * terminals and files that grow with the steps, as in the tutorial.
 */

const code = (text: string) => <code className="font-mono text-xs">{text}</code>;

// --- 1. Setup --------------------------------------------------------------------

const SETUP: Chunk[] = [
  { from: 0, code: "$ uv sync\nResolved 40 packages\n + vis-nav-sim==0.1.4" },
  {
    from: 1,
    code: "\n$ uv run source/keyboard_agent.py --local 7\ntexture pack ━━━━━━━━━━━━━━━━━━━━━━ 128.9/128.9 MB 4.0 MB/s\nrecording exploration data for local maze 7 into data/local-7 ...\nexploration data: data/local-7 (3212 frames on 3 routes)",
  },
  {
    from: 2,
    code: "\n╭─ local ───────────────────────────────────────────────────────────────────────╮\n│ Local maze 7                                                                  │\n│ no attempt spent, nothing recorded  ·  replay with motion_seed=4167007553     │\n│                                                                               │\n│ same maze anywhere:  run(agent, \"local:7\")                                    │\n╰───────────────────────────────────────────────────────────────────────────────╯",
  },
];

export const setupChapter: Chapter = {
  id: "setup",
  label: "Setup",
  title: "The simulator is already installed",
  intro: "The simulator the server runs is also a Python package, and the kit's uv sync installs it. The first run fetches the textures.",
  steps: [
    {
      title: "It came with uv sync",
      body: (
        <>
          {code("vis-nav-sim")} is in the kit&apos;s default groups, so the {code("uv sync")} from
          the tutorial installed it from the course index: the same maze generator, physics and
          renderer as the server, about 3 MB, for macOS, Windows and Linux. It renders on your
          GPU, or on the CPU if there is none; a 4-core machine with no GPU still gets 70 frames
          a second. If it is missing, run {code("uv sync")} again.
        </>
      ),
    },
    {
      title: "The texture pack downloads once",
      body: (
        <>
          The walls are 200 photos, 123 MB, kept out of the package. The first run downloads
          them into {code("~/.cache/vis-nav/")} with a progress bar and checks their fingerprint,
          the one every maze on the server records too. To keep them elsewhere, set{" "}
          {code("VIS_NAV_TEXTURES")} to the directory.
        </>
      ),
    },
    {
      title: "That is all",
      body: (
        <>
          The script runs as it would against the server, and the terminal says{" "}
          <span className="font-medium text-green-700 dark:text-green-400">local</span> in place
          of a session. Nothing was spent; nothing left your machine.
        </>
      ),
    },
  ],
  preview: (step) => <CodePanel chunks={SETUP} step={step} language="text" title="Terminal" />,
};

// --- 2. A seed is a maze -------------------------------------------------------

const SEEDS: Chunk[] = [
  { from: 0, code: "uv run source/keyboard_agent.py --local 7   # maze 7\nuv run source/my_agent.py --local 7\nuv run source/my_agent.py --local 2026      # another maze" },
  { from: 1, code: '\n# in code: the same run(), a seed instead of a challenge id\nresult = run(MyAgent(data), "local:7")' },
  { from: 2, code: "\n# or leave the command alone and move it with one variable\nVIS_NAV_SERVER=local uv run source/my_agent.py --challenge 515b23a3c14a" },
  { from: 3, code: "\n# what --local recorded, in the format a challenge hands out\ndata/local-7/\n  target.jpg\n  traj_0/  0.jpg 1.jpg ... data_info.json\n  traj_1/\n  traj_2/" },
];

export const seedsChapter: Chapter = {
  id: "seeds",
  label: "Seeds",
  title: "A seed is a maze",
  intro: "There is no fixed practice maze to download. The generator is deterministic, so a number is a maze, the same one on every machine.",
  steps: [
    {
      title: "Pick a number",
      body: (
        <>
          {code("--local 7")} generates maze 7 and puts the robot at its start. Any integer is a
          maze; {code("--local")} alone is 7. A friend who runs {code("--local 7")} is in the
          same maze, so seeds are shared like challenge ids. Generating a new one takes a
          millisecond.
        </>
      ),
    },
    {
      title: "In code, a string",
      body: (
        <>
          {code('run(agent, "local:7")')} is the same {code("run()")} the tutorial uses, with a
          seed where the challenge id goes. It does everything the server run does, apart from
          asking about attempts and opening a page.
        </>
      ),
    },
    {
      title: "Or one environment variable",
      body: (
        <>
          {code("VIS_NAV_SERVER=local")} sends an unchanged command to your machine: a bare
          integer as the challenge id is the seed, anything else is maze 7. Useful for trying a
          script exactly as you would run it for real.
        </>
      ),
    },
    {
      title: "Exploration data, recorded",
      body: (
        <>
          A challenge hands out photos of earlier drives through its maze. For a local maze the
          kit records them the first time, into {code("data/local-<seed>/")}, in the same
          format: three routes, a frame every five ticks, the goal photo. The baseline and
          anything you build on that data work unchanged.
        </>
      ),
    },
  ],
  preview: (step) => <CodePanel chunks={SEEDS} step={step} language="bash" title="Terminal" />,
};

// --- 3. What is different here ---------------------------------------------------

const TRUTH: Chunk[] = [
  { from: 0, code: 'from vis_nav_sdk import Action, connect\n\nwith connect("local:7") as session:\n    obs = session.step(Action.FORWARD, 4)' },
  { from: 1, code: "\n    x, y, yaw = session.pose()                  # where the robot really is\n    distance, heading = session.distance_heading()  # what a check-in would score" },
];

export const truthChapter: Chapter = {
  id: "truth",
  label: "Differences",
  title: "What is different here",
  intro: "Three things, all deliberate. Knowing them keeps a local habit from becoming a server surprise.",
  steps: [
    {
      title: "Nothing is spent or ranked",
      body: (
        <>
          No attempt, no key, no leaderboard. The check-in is still scored, with the rubric
          challenges use by default, so the result panel reads like a real one; the points mean
          nothing to anyone but you.
        </>
      ),
    },
    {
      title: "The truth is available",
      body: (
        <>
          {code("session.pose()")} returns the robot&apos;s position and heading;{" "}
          {code("distance_heading()")} what a check-in now would be measured on. A server session
          has neither, so code that calls them runs only here. Use them to check a localiser
          against the truth, then take them out.
        </>
      ),
    },
  ],
  preview: (step) => <CodePanel chunks={TRUTH} step={step} language="python" title="truth.py" />,
};

// --- 4. The simulator itself --------------------------------------------------------

const SIM: Chunk[] = [
  { from: 0, code: "import vis_nav_sim as sim\n\ntextures = sim.Textures.find()            # once per process\nworld = sim.Simulator(textures, seed=7)   # a maze" },
  { from: 1, code: "\nframe = world.initial_frame()             # (240, 320, 3) uint8 BGR\nviews = world.targets()                   # four goal photos\nframes = world.step(sim.FORWARD, repeat=4)  # hold it four ticks, one frame back" },
  { from: 2, code: "\nx, y, yaw = world.pose()\ndistance_m, heading_deg = world.distance_heading()\nworld.oracle_steps                        # the shortest route, in ticks: SPL's numerator\nworld.grid                                # rows of # and ." },
  { from: 3, code: "\nworld.reset(seed=8)                       # another maze, in about a millisecond\nworld.record(\"data/local-8\", routes=3)    # an exploration dataset of it" },
];

export const simulatorChapter: Chapter = {
  id: "simulator",
  label: "Simulator",
  title: "The simulator itself",
  intro: "Under the session is vis_nav_sim.Simulator: generate, step, look at the truth. For anything that wants thousands of frames a second, use it directly.",
  steps: [
    {
      title: "Textures once, mazes many",
      body: (
        <>
          {code("Textures.find()")} loads the pack onto the GPU; build one and share it.{" "}
          {code("Simulator(textures, seed)")} generates the maze and places the robot. Defaults are
          the fastest ones; {code("msaa=4")} renders the way a challenge does.
        </>
      ),
    },
    {
      title: "Step, and see",
      body: (
        <>
          {code("step(action, repeat)")} holds an action for that many ticks and returns the
          frames you asked for, as numpy arrays in the SDK&apos;s BGR order. On a laptop GPU a
          frame is half a millisecond. {code("jpeg()")} gives the exact bytes the server would
          send, if you want to train on what a run will see.
        </>
      ),
    },
    {
      title: "The truth, and the score",
      body: (
        <>
          {code("pose()")} and {code("distance_heading()")} as above, plus the shortest route the
          server grades SPL against and the occupancy grid. Everything the gateway keeps to
          itself is a property here.
        </>
      ),
    },
    {
      title: "Reset to another maze",
      body: (
        <>
          {code("reset(seed=8)")} swaps the maze without reloading the textures, so an episode per
          maze costs nothing. {code("record()")} writes an exploration dataset of the current maze
          in the server&apos;s format.
        </>
      ),
    },
  ],
  preview: (step) => <CodePanel chunks={SIM} step={step} language="python" title="simulator.py" />,
};

// --- 5. Reinforcement learning ---------------------------------------------------------

const RL: Chunk[] = [
  { from: 0, code: "$ uv sync --group rl          # gymnasium, skrl, PyPI's torch: CPU on macOS/Windows, CUDA on Linux\n$ uv sync --group rl-cuda     # or the CUDA build: an NVIDIA card on Windows or Linux\n$ uv sync --group rl-cpu      # or the CPU build everywhere: the small download" },
  { from: 1, code: "\n$ uv run source/rl/env.py --mazes 7,8,9    # a random policy, to see the environment\nmaze 7: 300 decisions, 1200 ticks, return -3.11, ended 5.39 m from the goal" },
  { from: 2, code: "\n$ uv run source/rl/train.py --mazes 7,8,9,10 --timesteps 200000\nsaved the policy to models/policy.pt; skrl's own checkpoints are under runs/" },
  { from: 3, code: "\n$ uv run source/rl/play.py --local 11        # a maze it never saw\n$ uv run source/rl/play.py --challenge <id>  # one real attempt" },
];

export const rlChapter: Chapter = {
  id: "rl",
  label: "RL (experimental)",
  title: "Reinforcement learning, an experimental demo",
  intro: "The kit's reference agent is the baseline from the tutorial. This chapter is a demo of something else the simulator makes possible: a Gymnasium environment and a PPO that trains on it. Out of the box it learns to move, not to find the goal. Treat it as plumbing to build on, not a path to a score.",
  steps: [
    {
      title: "Add an rl group",
      body: (
        <>
          {code("uv sync --group rl")} adds gymnasium and skrl, and with skrl, torch as PyPI
          ships it: the CPU build on macOS and Windows, the CUDA build on Linux (about 2.5 GB).
          Two more groups choose for you: {code("rl-cuda")} for an NVIDIA card on Windows or
          Linux, {code("rl-cpu")} for the small download anywhere. Pick one; uv refuses two.
        </>
      ),
    },
    {
      title: "The environment",
      body: (
        <>
          {code("source/rl/env.py")}: the observation is the camera frame and goal photo 0,
          downscaled to 80 × 60 and stacked; the action is one of four movements held for four
          ticks; the reward is the metres closed toward the goal, plus ten on arrival, minus a
          little per step. It uses the true position, which exists only here. An episode ends on
          arrival or after 500 decisions, and {code("reset()")} takes the next maze from a list.
        </>
      ),
    },
    {
      title: "Train",
      body: (
        <>
          {code("source/rl/train.py")} runs skrl&apos;s PPO with two small CNNs
          ({code("source/rl/models.py")}) over the mazes you list, two thousand decisions a second
          on a laptop GPU, and saves the policy&apos;s weights. Logs and checkpoints go under{" "}
          {code("runs/")} for TensorBoard.
        </>
      ),
    },
    {
      title: "Deploy the policy as an agent",
      body: (
        <>
          {code("source/rl/play.py")} wraps the saved policy in an ordinary agent that sees only
          frames, the situation on the server. {code("--local 11")} tries it on a maze it never
          trained on; {code("--challenge <id>")} spends a real attempt, which the demo policy
          will not score on. It checks in after a fixed number of decisions; a stopping rule, a
          better observation and a smarter reward are the first things to change if you take
          this further. The baseline remains the place to start.
        </>
      ),
    },
  ],
  preview: (step) => <CodePanel chunks={RL} step={step} language="text" title="Terminal" />,
};

export const LOCAL_CHAPTERS: Chapter[] = [
  setupChapter,
  seedsChapter,
  truthChapter,
  simulatorChapter,
  rlChapter,
];
